import convert from 'color-convert';

import { findClosestHueColorIndexFromList } from 'src/modules/designStudio/helpers/colorUtils';
import { VariationColorGroup } from 'src/modules/designStudio/helpers/productSelection/types';

export const AllColorGroups: VariationColorGroup[] = [
  VariationColorGroup.WHITE,
  VariationColorGroup.BEIGE,
  VariationColorGroup.GRAY,
  VariationColorGroup.BLACK,
  VariationColorGroup.GREEN,
  VariationColorGroup.BLUE,
  VariationColorGroup.YELLOW,
  VariationColorGroup.RED,
  VariationColorGroup.PURPLE,
  VariationColorGroup.BROWN,
];

export const ColorGroupNames: Record<VariationColorGroup, string> = {
  [VariationColorGroup.WHITE]: 'White',
  [VariationColorGroup.BEIGE]: 'Beige',
  [VariationColorGroup.GRAY]: 'Gray',
  [VariationColorGroup.BLACK]: 'Black',
  [VariationColorGroup.GREEN]: 'Green',
  [VariationColorGroup.BLUE]: 'Blue',
  [VariationColorGroup.YELLOW]: 'Yellow',
  [VariationColorGroup.RED]: 'Red',
  [VariationColorGroup.PURPLE]: 'Purple',
  [VariationColorGroup.BROWN]: 'Brown',
};

export const ColorGroupDisplayHex: Record<VariationColorGroup, string> = {
  [VariationColorGroup.WHITE]: '#ffffff',
  [VariationColorGroup.BEIGE]: '#D6CDBA',
  [VariationColorGroup.GRAY]: '#8E9395',
  [VariationColorGroup.BLACK]: '#252728',
  [VariationColorGroup.GREEN]: '#4D6B5B',
  [VariationColorGroup.BLUE]: '#4D5B6B',
  [VariationColorGroup.YELLOW]: '#DBC68E',
  [VariationColorGroup.RED]: '#AF6161',
  [VariationColorGroup.PURPLE]: '#806398',
  [VariationColorGroup.BROWN]: '#957D6A',
};

export const ColorGroupMatchingHex: Record<VariationColorGroup, string> = {
  [VariationColorGroup.WHITE]: '#ffffff',
  [VariationColorGroup.BEIGE]: '#D6CDBA',
  [VariationColorGroup.GRAY]: '#838383',
  [VariationColorGroup.BLACK]: '#000000',
  [VariationColorGroup.GREEN]: '#4D6B5B',
  [VariationColorGroup.BLUE]: '#4D5B6B',
  [VariationColorGroup.YELLOW]: '#DBC68E',
  [VariationColorGroup.RED]: '#AF6161',
  [VariationColorGroup.PURPLE]: '#806398',
  [VariationColorGroup.BROWN]: '#957D6A',
};

export const ColorGroupRankings: Record<VariationColorGroup, number> = {
  [VariationColorGroup.WHITE]: 11,
  [VariationColorGroup.BEIGE]: 10,
  [VariationColorGroup.GRAY]: 9,
  [VariationColorGroup.BLACK]: 8,
  [VariationColorGroup.GREEN]: 7,
  [VariationColorGroup.BLUE]: 6,
  [VariationColorGroup.YELLOW]: 5,
  [VariationColorGroup.RED]: 4,
  [VariationColorGroup.PURPLE]: 3,
  [VariationColorGroup.BROWN]: 1,
};

export const OverrideColors = {
  [VariationColorGroup.WHITE]: ['#ffffff', '#FCFCFC', '#f8f8f8', '#FFFFFF'],
  [VariationColorGroup.BEIGE]: [],
  [VariationColorGroup.GRAY]: [
    '#43433B',
    '#9ca4a7',
    '#A4AAAD',
    '#A4A9AC',
    '#7c858c',
    '#CFD7D7',
    '#798689',
    '#ADBCC1',
    '#859298',
    '#DCE0E1',
    '#BFC8C9',
    '#576162',
    '#616D74',
    '#505861',
    '#414848',
    '#505859',
    '#98A0AC',
    '#414748',
    '#737C80',
    '#949ca1',
    '#d8dee5',
    '#bfc8c9',
    '#707b7a',
    '#879794',
    '#2B352F',
  ],
  [VariationColorGroup.BLACK]: [
    '#171717',
    '#001414',
    '#242323',
    '#333636',
    '#3a3232',
    '#2e2b2a',
  ],
  [VariationColorGroup.GREEN]: [
    '#9ca982',
    '#697552',
    '#728164',
    '#294A4C',
    '#272927',
    '#97A3A2',
  ],
  [VariationColorGroup.BLUE]: [],
  [VariationColorGroup.YELLOW]: [],
  [VariationColorGroup.RED]: [
    '#c90f19',
    '#a11d21',
    '#972C32',
    '#641C27',
    '#933c06',
  ],
  [VariationColorGroup.PURPLE]: [],
  [VariationColorGroup.BROWN]: [
    '#261F13',
    '#544441',
    '#412E2E',
    '#835033',
    '#422E2C',
    '#996E3F',
    '#432a1b',
    '#3f1b00',
    '#301e00',
    '#6a2304',
  ],
};

const getColorOverride = (color: string): VariationColorGroup | undefined =>
  AllColorGroups.find(group => OverrideColors[group].find(c => c === color));

export const categorizeColor = (color: string): VariationColorGroup => {
  /**
   * The algorithm that categorizes color simply doesn't work for certain colors, the
   * variable OverrideColors has been manually created to correct colors that are not
   * categorized correctly.
   */
  const colorOverride = getColorOverride(color);
  if (colorOverride) {
    return colorOverride;
  }

  const colorHSL = convert.hex.hsl(color);
  if (colorHSL[1] < 5) {
    return VariationColorGroup.GRAY;
  }
  const colorEntries: [string, string][] = Object.entries(
    ColorGroupMatchingHex,
  );
  const closestIndex = findClosestHueColorIndexFromList(
    color,
    colorEntries.map(([_, value]) => value),
  );
  return closestIndex
    ? (colorEntries[closestIndex][0] as VariationColorGroup)
    : VariationColorGroup.BEIGE;
};
